import { ReactNode, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { buttonVariants } from "~/components/ui/button";

interface ConfirmDestructiveDialogProps<T> {
  // The thing to be destroyed. `null` closes the dialog, so callers keep one
  // piece of state instead of a target and an `open` flag that can disagree.
  target: T | null;
  title: string;
  description: (target: T) => ReactNode;
  confirmLabel: string;
  pendingLabel: string;
  // Reports its own failure to the user. Rejecting keeps the dialog open.
  onConfirm: (target: T) => Promise<void>;
  onClose: () => void;
  // Rendered between the description and the actions, for callers that show the
  // failure in the dialog rather than in a toast.
  error?: ReactNode;
}

/**
 * Confirmation for an action that destroys something, owning the in-flight state
 * so a slow delete cannot be fired twice. Closes itself once `onConfirm`
 * resolves; a rejection leaves it open for a retry.
 */
export function ConfirmDestructiveDialog<T>({
  target,
  title,
  description,
  confirmLabel,
  pendingLabel,
  onConfirm,
  onClose,
  error,
}: ConfirmDestructiveDialogProps<T>) {
  const [pending, setPending] = useState(false);

  const handleConfirm = async (e: React.MouseEvent) => {
    // Radix closes on click; the dialog has to outlive the request to show progress.
    e.preventDefault();
    if (target === null) {
      return;
    }
    setPending(true);
    try {
      await onConfirm(target);
      onClose();
    } catch {
      // `onConfirm` has already told the user what went wrong.
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={target !== null} onOpenChange={(open) => !open && !pending && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{target !== null && description(target)}</AlertDialogDescription>
        </AlertDialogHeader>
        {error}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={pending}
            className={buttonVariants({ variant: "destructive" })}
          >
            {pending ? pendingLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
